import { Component, EventEmitter, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as moment from 'moment';
import { BusinessTransactionType } from '../../foundation/models/enums/business-transaction-type.enum';
import { BaseComponent } from '../../shared/base-components/base.component';
import { getCurrentDate, isStartAfterEnd, setServerDate } from '../../shared/helpers/time-helpers.model';
import { DocumentItemKeyData } from '../../shared/models/document/document-itemkey.model';
import { LocationService } from '../../shared/services/location/location.service';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { ControlValidationMessagesService } from '../../shared/validators/control-validation-messages.service';
import { WIPStatusType } from '../../wip/models/enums/w-i-p-status-type.enum';
import { ServiceHistoryResultsComponent } from './service-history-results.component';

@Component({
    selector: 'service-history',
    templateUrl: './service-history.html',
    inputs: ['customerNumber']
})

export class ServiceHistoryComponent extends BaseComponent {

    @ViewChild('serviceHistoryResults') serviceHistoryResults: ServiceHistoryResultsComponent;

    public form: UntypedFormGroup;
    public customerNumber: any;
    public title: EventEmitter<any> = new EventEmitter<any>();
    public wipStatusType: WIPStatusType = new WIPStatusType();
    public documentUploadItems: DocumentItemKeyData[] = [];

    public inProgress: boolean;
    public maxEndDate: Date;
    public maxStartDate: Date;

    /**
    * Enum for all business transaction types.
    */
    private businessTransactionType: BusinessTransactionType = new BusinessTransactionType();

    // Gets or sets the business unit list
    public businessUnits: any[];

    // Gets or sets the business transaction type list
    public businessTransactionTypes: any[];

    constructor(private fb: UntypedFormBuilder,
        public modalService: MvdModalService,
        private locationService: LocationService) {
        super();
    }

    async ngOnInit() {

        this.inProgress = true;

        await this.constructFormBuilder();

        this.inProgress = false;
    }

    private async constructFormBuilder() {

        this.form = this.fb.group({
            customerNumberId: [this.customerNumber],
            startDate: [setServerDate(moment(getCurrentDate()).subtract(7, 'day').toDate()), Validators.compose([ControlValidationMessagesService.minDateValidator])],
            endDate: [setServerDate(moment(getCurrentDate()).toDate()), Validators.compose([ControlValidationMessagesService.minDateValidator])],
            businessUnitId: [null],
            businessTransactionTypeId: [null],
            receiptId: [null, Validators.compose([ControlValidationMessagesService.positiveNumber])]
        },
        {
            validators: this.dateValidator
        });

        this.getBusinessTransactionTypes();
        await this.getBusinessUnits();
        this.setDefaultControls();
    }

    private dateValidator(group: UntypedFormGroup): ValidationErrors | null {
        const startDate = group.get('startDate').value;
        const endDate = group.get('endDate').value;
        if (isStartAfterEnd(startDate, endDate)) {
            return { 'startAfterEndDate': true };
        }
        return null;
    }

    private setEndDateMaxValue() {
        const endDate = moment(this.form.get('startDate').value).add(7, 'day');
        this.maxEndDate = (endDate.diff(moment(getCurrentDate()), 'days') > 0) ? getCurrentDate() : setServerDate(endDate.toDate());
        this.form.get('endDate').setValue(this.maxEndDate);
    }

    setDefaultControls() {
        this.form.controls['customerNumberId'].setValue(this.customerNumber);
        this.form.controls['startDate'].setValue(setServerDate(moment(getCurrentDate()).subtract(7, 'day').toDate()));
        this.form.controls['endDate'].setValue(setServerDate(moment(getCurrentDate()).toDate()));
        this.setEndDateMaxValue();
        this.maxStartDate = getCurrentDate();
    }

    /**
    * Handles the on start date changed event of the date picker
    * and sets the end date and max end date value
    */
    public onStartDateChanged(event) {
        this.setEndDateMaxValue();
    }

    resetSearch() {
        this.form.reset();
        this.setDefaultControls();
        this.serviceHistoryResults.resetPagination();
    }

    search() {
        this.serviceHistoryResults.resetPagination();
        this.serviceHistoryResults.populateSearchResults();
    }

    /**
   * Gets the business transaction types
   */
    private getBusinessTransactionTypes() {
        const result = this.businessTransactionType.GetActive();
        this.businessTransactionTypes = [];
        for (let businessTransactionType of result) {
            this.businessTransactionTypes.push({ value: businessTransactionType.id.toString(), label: businessTransactionType.displayValue.toString() });
        }

        // Manually insert empty business transaction type to drop down list
        const businessTransactionType = { value: null, label: '' };
        this.businessTransactionTypes.unshift(businessTransactionType);
    }

    /**
     * Gets the current business units.
     */
    private async getBusinessUnits() {
        var result = await this.locationService.getActiveBusinessUnits().toPromise();

        result.result.forEach(businessUnit => {
            businessUnit.value = businessUnit.id;
            businessUnit.label = '(' + businessUnit.businessUnitCode + ') ' + businessUnit.businessUnitName;
        });

        this.businessUnits = result.result;
        // Manually insert empty business unit to drop down list
        const businessUnit = { value: null, label: '' };
        this.businessUnits.unshift(businessUnit);
    }
}
